import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import * as Sentry from "@sentry/react";
import Banner from "components/Banner";
import {
  ERROR_GENERAL,
  ERROR_LOGIN_EMAIL_UNVERIFIED,
  WARNING_INTERNET_EXPLORER,
} from "constants/messages";
import { HOME, PROTECTED } from "constants/routes";
import {
  getAuth,
  getMultiFactorResolver,
  signInWithEmailAndPassword,
} from "firebase/auth";
import useIsMounted from "ismounted";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { checkIsEmailVerified } from "utils/ui";
import LoginForm from "views/Auth/LoginForm";
import MfaAuth from "views/Auth/Mfa/MfaAuth";
import MfaRegister from "views/Auth/Mfa/MfaRegister";

/* global grecaptcha */

export default function Login() {
  const { enqueueSnackbar } = useSnackbar();
  const isMounted = useIsMounted();
  const auth = getAuth();
  let location = useLocation();
  const navigate = useNavigate();

  const isExplorer = !!document.documentMode;

  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");

  const [nextUrl, setNextUrl] = useState(PROTECTED);

  const [loading, setLoading] = useState(false);
  const [loginSubmitted, setLoginSubmitted] = useState(false);

  const [mfaAction, setMfaAction] = useState(null);

  const [redirect, setRedirect] = useState(null);
  const [resolver, setResolver] = useState(null);

  const handleChange = (event) => {
    if (event.target.name === "user") {
      setUser(event.target.value);
    } else if (event.target.name === "password") {
      setPassword(event.target.value);
    }
  };

  const renderRecaptcha = () => {
    window.recaptchaVerifier
      .render()
      .then((widgetId) => {
        window.recaptchaWidgetId = widgetId;
      })
      .catch(() => {});
  };

  const resetRecaptcha = () => {
    grecaptcha.reset(window.recaptchaWidgetId);
  };

  useEffect(() => {
    const handleLogin = () => {
      setLoading(true);
      signInWithEmailAndPassword(auth, user.toLowerCase(), password)
        .then((response) => {
          if (isMounted.current) {
            resetRecaptcha();
            setLoading(false);
            if (response.user) {
              if (checkIsEmailVerified(response.user.emailVerified)) {
                setMfaAction("register");
              } else {
                enqueueSnackbar(ERROR_LOGIN_EMAIL_UNVERIFIED, {
                  variant: "error",
                });
              }
            } else {
              setLoading(false);
              enqueueSnackbar(ERROR_GENERAL, { variant: "error" });
            }
          }
        })
        .catch((error) => {
          if (error.code == "auth/multi-factor-auth-required") {
            // The user is a multi-factor user. Second factor challenge is required.
            let _resolver = getMultiFactorResolver(auth, error);
            setResolver(_resolver);

            setLoading(false);
            setMfaAction("authenticate");
          } else {
            // Handle other errors such as wrong password.
            setLoading(false);
            resetRecaptcha();
            let message = "Error logging in. Please try again.";
            if (error.code === "auth/user-not-found") {
              message =
                "User not found. Please contact an admin for further support.";
            } else if (error.code === "auth/wrong-password") {
              message = "Wrong password. Please try again.";
            } else if (error.code === "auth/user-disabled") {
              message =
                "User disabled. Please contact an admin for further support.";
            } else if (error.code === "auth/too-many-requests") {
              message =
                "Too many login attemps. Please wait before logging in again.";
            } else {
              Sentry.captureException(error);
            }
            enqueueSnackbar(message, {
              variant: "error",
            });
          }
        });
    };

    if (loginSubmitted) {
      // Fetch-on-mount pattern; React 19 still supports this for initial data loading.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoginSubmitted(false);
      handleLogin();
    }
    // eslint-disable-next-line
  }, [enqueueSnackbar, loginSubmitted, isMounted]);

  useEffect(() => {
    if (location && location.state && location.state.returnUrl) {
      if (location.state.returnUrl === PROTECTED) {
        // Redirect-state handoff; React 19 still supports setState in effect for this one-shot init.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setNextUrl(PROTECTED);
        setTimeout(() => {
          enqueueSnackbar("You've been signed out.", { variant: "success" });
        }, 300);
      } else if (
        location.state.returnUrl !== PROTECTED &&
        location.state.returnUrl !== HOME
      ) {
        setNextUrl(location.state.returnUrl);
        setTimeout(() => {
          enqueueSnackbar("Please log in to access this page.", {
            variant: "warning",
          });
        }, 300);
      }
    }
    navigate(location.pathname, {});
    // eslint-disable-next-line
  }, []);

  if (redirect) {
    return (
      <Navigate
        to={PROTECTED}
        state={{ login: "success", returnUrl: nextUrl }}
        replace
      />
    );
  }
  return (
    <>
      {isExplorer && (
        <Banner message={WARNING_INTERNET_EXPLORER} icon={PriorityHighIcon} />
      )}
      <Container maxWidth="xs">
        <Box sx={{ mt: 8, mb: 8 }}>
          {mfaAction === null && (
            <LoginForm
              isExplorer={isExplorer}
              user={user}
              loading={loading}
              handleChange={handleChange}
              setLoginSubmitted={setLoginSubmitted}
              renderRecaptcha={renderRecaptcha}
            />
          )}
          {mfaAction === "authenticate" && (
            <MfaAuth resolver={resolver} setRedirect={setRedirect} />
          )}
          {mfaAction === "register" && (
            <MfaRegister setRedirect={setRedirect} />
          )}
        </Box>
      </Container>
    </>
  );
}
